/**
 * @fileoverview API configuration for KaraoNow frontend.
 * @summary Centralizes API base URL and endpoint configuration.
 *
 * @package KaraoNow
 * @module apps/front/src/config/api
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

const envBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim()
const runtimeOrigin =
  typeof window !== 'undefined' && window.location?.origin ? window.location.origin : undefined
const resolvedBaseUrl = envBaseUrl || runtimeOrigin || 'http://localhost:3000'

/**
 * @summary Base URL for the backend REST API.
 * @description Falls back to the deployment origin (or localhost) when VITE_API_BASE_URL is not provided.
 * @constant
 * @type {string}
 */
export const API_BASE_URL: string = `${resolvedBaseUrl.replace(/\/$/, '')}/api/v1`

/**
 * @summary API endpoints collection.
 * @constant
 * @type {object}
 */
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    REGISTER: `${API_BASE_URL}/auth/register`,
    SET_PASSWORD: `${API_BASE_URL}/auth/set-password`,
    LOGIN: `${API_BASE_URL}/auth/login`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
    REFRESH: `${API_BASE_URL}/auth/refresh`,
    ME: `${API_BASE_URL}/auth/me`,
    CSRF_TOKEN: `${API_BASE_URL}/auth/csrf-token`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
  },
  // Users
  USERS: {
    PREFERENCES: `${API_BASE_URL}/users/me/preferences`,
    EVENTS: `${API_BASE_URL}/users/me/events`,
    HISTORY: `${API_BASE_URL}/users/me/history`,
    STATS: `${API_BASE_URL}/users/me/stats`,
    EXPORT_DATA: `${API_BASE_URL}/users/me/data`,
    DELETE_ACCOUNT: `${API_BASE_URL}/users/me`,
    REQUEST_EMAIL_CHANGE: `${API_BASE_URL}/users/me/email/request-change`,
    CONFIRM_EMAIL_CHANGE: (token: string) => `${API_BASE_URL}/users/me/email/confirm-change/${token}`,
  },
  // Events
  EVENTS: {
    LIST: `${API_BASE_URL}/events`,
    CREATE: `${API_BASE_URL}/events`,
    GET: (id: string) => `${API_BASE_URL}/events/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/events/${id}`,
    HIDE: (id: string) => `${API_BASE_URL}/events/${id}/hide`,
    DELETE: (id: string) => `${API_BASE_URL}/events/${id}`,
    CHECK_QUOTA: (id: string) => `${API_BASE_URL}/events/${id}/check-quota`,
    CHECK_QUOTA_CREATE: `${API_BASE_URL}/events/check-quota`,
    QR_CODE: (id: string) => `${API_BASE_URL}/events/${id}/qrcode`,
    QR_VALIDATE: (token: string) => `${API_BASE_URL}/events/qr/${token}`,
    JOIN: (eventId: string) => `${API_BASE_URL}/events/${eventId}/join`,
    SINGERS: (eventId: string) => `${API_BASE_URL}/events/${eventId}/singers`,
    REMOVE_SINGER: (eventId: string, singerId: string) => `${API_BASE_URL}/events/${eventId}/singers/${singerId}`,
    PUBLIC_INFO: (eventId: string) => `${API_BASE_URL}/events/${eventId}/public`,
  },
  // Venues
  VENUES: {
    LIST: `${API_BASE_URL}/venues`,
    CREATE: `${API_BASE_URL}/venues`,
    GET: (id: string) => `${API_BASE_URL}/venues/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/venues/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/venues/${id}`,
  },
  // Queue
  QUEUE: {
    GET: (eventId: string) => `${API_BASE_URL}/events/${eventId}/queue`,
    ADD: (eventId: string) => `${API_BASE_URL}/events/${eventId}/queue`,
    UPDATE_POSITION: (eventId: string, songId: string) =>
      `${API_BASE_URL}/events/${eventId}/queue/${songId}/position`,
    UPDATE_STATUS: (eventId: string, songId: string) =>
      `${API_BASE_URL}/events/${eventId}/queue/${songId}/status`,
    DELETE: (eventId: string, songId: string) =>
      `${API_BASE_URL}/events/${eventId}/queue/${songId}`,
  },
  // Songs
  SONGS: {
    SEARCH: `${API_BASE_URL}/songs/search`,
    SEARCH_LOCAL: `${API_BASE_URL}/songs/search/local`,
    GET: (id: string) => `${API_BASE_URL}/songs/${id}`,
    ADD: `${API_BASE_URL}/songs`,
  },
  // Sessions
  SESSIONS: {
    CREATE: `${API_BASE_URL}/sessions`,
    GET: (token: string) => `${API_BASE_URL}/sessions/${token}`,
    UPDATE: (token: string) => `${API_BASE_URL}/sessions/${token}`,
    DELETE: (token: string) => `${API_BASE_URL}/sessions/${token}`,
  },
  // Subscription
  SUBSCRIPTION: {
    PLANS: `${API_BASE_URL}/subscription/plans`,
    CURRENT: `${API_BASE_URL}/subscription/current`,
  },
  // Payments
  PAYMENTS: {
    HISTORY: `${API_BASE_URL}/payments/history`,
  },
  // Super Admin
  SUPERADMIN: {
    USERS: `${API_BASE_URL}/superadmin/users`,
    EVENTS: `${API_BASE_URL}/superadmin/events`,
    STATS: `${API_BASE_URL}/superadmin/stats`,
  },
} as const
